import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useCookies } from 'hooks';

import { useUserContext } from './UserContext';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { COOKIE } from '../helpers/constants';
import getConfig from 'next/config';
import { trackBucketing } from '../helpers/experiments/tracking/trackBucketing';
import { getPricingIndicatorBucketedCookie } from '../components/PricingIndicator/constants';
import { getCtaDistributionBucketedCookie } from '../helpers/experiments/ctaDistributionExperiment.constants';
import { type CtaDistributionVariation } from '../helpers/experiments/ctaDistributionExperiment';
import { getGalleryInteractionsBucketedCookie } from '../helpers/experiments/galleryInteractionsExperiment.constants';
import { GALLERY_INTERACTIONS_EXPERIMENT_KEY } from '../helpers/experiments/constants';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = {
  children: ReactNode;
  showPricingIndicator?: boolean;
  pricingIndicatorVariationKey?: string | null;
  ctaDistributionVariationKey?: CtaDistributionVariation;
  galleryInteractionsVariationKey?: string | null;
};

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
  showPricingIndicator: boolean;
  pricingIndicatorVariationKey: string | null;
  ctaDistributionVariationKey: CtaDistributionVariation;
  galleryInteractionsVariationKey: string | null;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const {
    children,
    showPricingIndicator = false,
    pricingIndicatorVariationKey = null,
    ctaDistributionVariationKey = null,
    galleryInteractionsVariationKey = null,
  } = params;

  const {
    publicRuntimeConfig: {
      PRICING_INDICATOR_EXPERIMENT_KEY,
      CTA_DISTRIBUTION_EXPERIMENT_KEY,
    },
  } = getConfig();

  const { get } = useCookies({ name: COOKIE.DD_CLIENT_ID });

  const ddClientId = get();

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof ddClientId === 'string' &&
      ddClientId.trim().length > 0
    ) {
      initSdk(ddClientId);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, ddClientId]);

  // The pricing indicator experiment is bucketed server-side to avoid a flash of content.
  // We track the bucketing event client-side directly — setting a cookie (so the server
  // knows on subsequent requests) and pushing the GTM event. trackBucketing deduplicates
  // via the cookie, so this is safe to call on every mount.
  useEffect(() => {
    if (!pricingIndicatorVariationKey) return;

    trackBucketing({
      cookieName: getPricingIndicatorBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: PRICING_INDICATOR_EXPERIMENT_KEY,
      variationKey: pricingIndicatorVariationKey,
    });
  }, [pricingIndicatorVariationKey, PRICING_INDICATOR_EXPERIMENT_KEY]);

  useEffect(() => {
    if (!ctaDistributionVariationKey) return;

    trackBucketing({
      cookieName: getCtaDistributionBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: CTA_DISTRIBUTION_EXPERIMENT_KEY,
      variationKey: ctaDistributionVariationKey,
    });
  }, [ctaDistributionVariationKey, CTA_DISTRIBUTION_EXPERIMENT_KEY]);

  useEffect(() => {
    if (!galleryInteractionsVariationKey) return;

    trackBucketing({
      cookieName: getGalleryInteractionsBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: GALLERY_INTERACTIONS_EXPERIMENT_KEY,
      variationKey: galleryInteractionsVariationKey,
    });
  }, [galleryInteractionsVariationKey]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
      showPricingIndicator,
      pricingIndicatorVariationKey,
      ctaDistributionVariationKey,
      galleryInteractionsVariationKey,
    }),
    [
      sdkContext,
      showPricingIndicator,
      pricingIndicatorVariationKey,
      ctaDistributionVariationKey,
      galleryInteractionsVariationKey,
    ],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
